import { motion } from "framer-motion";
import { Eye, Target, Users, Award, Shield, Leaf } from "lucide-react";
import { useSiteSettings, type SiteSettings } from "@/hooks/useSiteSettings";

const AboutSection = () => {
  const { data } = useSiteSettings();
  const settings = data as SiteSettings | null;
  
  const stats = settings?.home_stats || [
    { icon: Users, value: "+1000", label: "عميل سعيد" },
    { icon: Award, value: "+80", label: "منتج طبيعي" },
    { icon: Shield, value: "100%", label: "جودة مضمونة" },
    { icon: Leaf, value: "طبيعي", label: "بدون مواد حافظة" },
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/3 rounded-full blur-[100px]" />
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/3 rounded-full blur-[80px]" />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
          <motion.div
            className="flex-1 text-right"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-cairo font-extrabold text-foreground mb-4">
              <span className="text-gradient">من نحن</span>
            </h2>
            <p className="text-muted-foreground font-cairo text-lg leading-relaxed">
              جنّة هي شركة رائدة في مجال المنتجات الغذائية الأساسية بالجملة في مصر، متخصصة في توفير أجود أنواع الدقيق والسكر والأرز والمكرونات والتوابل والبهارات بأسعار تنافسية وجودة عالية لكل بيت مصري.
            </p>
          </motion.div>

          {settings?.about_image && (
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img src={settings.about_image} alt="عن جنّة" className="rounded-2xl shadow-2xl w-full h-[300px] object-cover" />
            </motion.div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="bg-card rounded-2xl p-6 text-center border border-border group hover:border-primary/30 transition-all duration-300 relative overflow-hidden"
              style={{ boxShadow: "var(--card-shadow)" }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              whileHover={{ y: -4, scale: 1.02 }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 relative z-10" style={{ background: "var(--premium-gradient)" }}>
                <stat.icon className="w-5 h-5 text-primary-foreground" />
              </div>
              <p className="text-2xl md:text-3xl font-cairo font-extrabold text-gradient relative z-10">{stat.value}</p>
              <p className="text-sm font-cairo text-muted-foreground mt-1 relative z-10">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {[
            { icon: Eye, title: "رؤيتنا", text: "أن نكون الخيار الأول للمنتجات الغذائية الطبيعية في مصر والوطن العربي، من خلال تقديم منتجات عالية الجودة من مصادر طبيعية موثوقة." },
            { icon: Target, title: "مهمتنا", text: "توفير منتجات غذائية طبيعية وصحية تجعل حياتك أفضل، مع الالتزام بأعلى معايير الجودة والسلامة الغذائية وخدمة عملاء متميزة." },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              className="bg-card rounded-2xl p-8 border border-border group hover:border-primary/30 transition-all duration-300 relative overflow-hidden"
              style={{ boxShadow: "var(--card-shadow)" }}
              initial={{ opacity: 0, x: i === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 relative z-10" style={{ background: "var(--premium-gradient)" }}>
                <item.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-cairo font-bold text-foreground mb-3 relative z-10">{item.title}</h3>
              <p className="text-muted-foreground font-cairo leading-relaxed relative z-10">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
